import { z } from "zod";
import { userAreaSchema, userRolesSchema } from "./routineAreas.ts";

export const skillKindResp = z.enum(["system", "override", "user"]);

export const skillResp = z.object({
  name: z.string(),
  description: z.string(),
  /**
   * Server-side derived: `true` when the row has an amendment or a pinned
   * description. Drives the "Edited" badge on the list view without forcing the
   * list endpoint to ship 20KB×N bodies.
   */
  isEdited: z.boolean(),
  /**
   * Empty in the list response (kept lean — see handler comment). Populated by
   * `GET /a/skills/:name` for the edit page. For a system skill this is the
   * shipped text and the editor shows it read-only.
   */
  body: z.string(),
  /**
   * The user's additions, shown in the editable box beside the read-only body.
   * Absent on user-authored skills, which own their body outright.
   */
  amendment: z.string().optional(),
  kind: skillKindResp,
  enabled: z.boolean(),
  entId: z.string().nullable(),
  /**
   * The skill's area id, and the role ids it suits (empty: every role).
   * Strings, not the enums of `routineAreas.ts`: an app build parses this, so
   * an area or role added later must not fail an older build's parse.
   * Optional for the same reason, and because a user's own skill may have none.
   */
  area: z.string().optional(),
  roles: z.array(z.string()).optional(),
});

export const listSkillsResp = z.array(skillResp);

// Validation rules shared between the HTTP handler (zValidator) and the
// Forthic agent module so the agent and user inputs go through the same
// shape gate before reaching the CRUD layer.
export const SKILL_NAME_PATTERN = /^[a-z0-9-]+$/;
export const SKILL_NAME_MIN = 2;
export const SKILL_NAME_MAX = 50;
export const SKILL_DESCRIPTION_MAX = 200;
export const SKILL_BODY_MAX = 20480;
// Kept in step with the routine amendment cap. Still far below SKILL_BODY_MAX
// because an amendment adjusts shipped guidance rather than replacing it.
export const SKILL_AMENDMENT_MAX = 5000;

/**
 * Skill names that would collide with frontend routes or other reserved
 * paths. `/skills/new` is the create-form route, so a user skill named
 * "new" would shadow it.
 */
export const RESERVED_SKILL_NAMES: ReadonlySet<string> = new Set(["new"]);

/**
 * Reduce free-text into the wire format `skillNameSchema` enforces
 * (`^[a-z0-9-]+$`). Lowercases, collapses any non-alphanumeric run to a single
 * hyphen, and trims leading/trailing hyphens. "Clear Inbox" → "clear-inbox",
 * "  v2 Foo!  " → "v2-foo".
 *
 * Shared rather than per-caller: the create form slugs what the user types and
 * the agent module slugs what the model proposes, and the two landing on
 * different names for the same words would put a skill where neither could find
 * it again. Note this can return `""` (input with no alphanumerics) — callers
 * validate the result, they don't assume it.
 */
export function toSkillNameSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export const skillNameSchema = z
  .string()
  .min(SKILL_NAME_MIN)
  .max(SKILL_NAME_MAX)
  .regex(
    SKILL_NAME_PATTERN,
    "Skill names use lowercase letters, digits, and hyphens only (e.g. clear-inbox)",
  )
  .refine((s) => !RESERVED_SKILL_NAMES.has(s), {
    message: "This name is reserved — pick another.",
  });

/** Body for `POST /a/skills` — create a user-authored skill. */
export const createUserSkillReq = z.object({
  name: skillNameSchema,
  description: z.string().min(1).max(SKILL_DESCRIPTION_MAX),
  body: z.string().min(1).max(SKILL_BODY_MAX),
  /** A built-in area id or a name the user added, and the roles the skill suits. */
  area: userAreaSchema.optional(),
  roles: userRolesSchema.optional(),
});

/**
 * Body for `POST /a/skills/:name` — partial update of any skill ent's content.
 *
 * `body` applies only to a user-authored skill. For a system skill the shipped
 * text is not editable and `amendment` carries the user's changes instead; a
 * `body` sent for one is ignored rather than rejected, so a frontend deployed
 * before amendments existed still saves cleanly.
 */
export const updateSkillReq = z.object({
  description: z.string().min(1).max(SKILL_DESCRIPTION_MAX).optional(),
  body: z.string().min(1).max(SKILL_BODY_MAX).optional(),
  amendment: z.string().max(SKILL_AMENDMENT_MAX).optional(),
  enabled: z.boolean().optional(),
  /**
   * A user-authored skill's area; `null` clears it. Like `body`, ignored for a
   * system skill, whose area is the shipped one.
   */
  area: userAreaSchema.nullish(),
  roles: userRolesSchema.optional(),
});

export type SkillKindResp = z.infer<typeof skillKindResp>;
export type SkillResp = z.infer<typeof skillResp>;
export type ListSkillsResp = z.infer<typeof listSkillsResp>;
export type CreateUserSkillReq = z.infer<typeof createUserSkillReq>;
export type UpdateSkillReq = z.infer<typeof updateSkillReq>;
